import {ReactElement, useState} from 'react';
import {AlgoliaProductCard, IconEdit, IconTrack} from '~/components';
import {ProductHit} from '~/types/algoliaTypes';

import {
  Configure,
  InfiniteHits,
  InstantSearch,
  SortBy,
  useInstantSearch,
  // @ts-ignore:next-line
} from 'react-instantsearch';
import {
  algoliaClient,
  productsIndexName,
} from '../global/AlgoliaProvider.client';
import {CurrentFilters} from './CurrentFilters';
import {NumberOfHits} from './NumberOfHits.client';
import {SanityCollectionTree} from '~/types/sanityTypes';
import {getSlugsFromCollectionTreeArray} from '~/lib';
import {Collection as ShopifyCollection} from '@shopify/hydrogen/storefront-api-types';
import {FiltersContainer} from './FiltersContainer.client';

export default function InstantSearchCollection({
  handle,
  collectionTree,
  peerCollections,
  shopifyCollection,
}: {
  handle: string;
  collectionTree: SanityCollectionTree[] | null;
  peerCollections?: SanityCollectionTree[] | null;
  shopifyCollection: ShopifyCollection;
}) {
  // Get all subcollections for the filter parameter: collections:audio OR collections:kaiuttimet OR...
  const filterCollections = collectionTree
    ? getSlugsFromCollectionTreeArray(collectionTree)
    : [handle];

  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <InstantSearch searchClient={algoliaClient} indexName={productsIndexName}>
      <>
        <Configure
          filters={'collections:' + filterCollections.join(' OR collections:')}
          hitsPerPage={24}
          distinct={false}
        />
        <div className="container pt-6 pb-16">
          <div className="grid grid-cols-12">
            <div className="flex flex-col col-span-12 text-sm lg:col-span-3 lg:pb-8 lg:pr-6">
              <FiltersContainer
                handle={handle}
                filtersOpen={filtersOpen}
                setFiltersOpen={setFiltersOpen}
                peerCollections={peerCollections}
              />
            </div>
            <div className="grid col-span-12 lg:col-span-9 pb-8">
              <div className="collection-sorting text-textSecondary text-sm lg:flex lg:justify-between lg:items-center">
                <NumberOfHits />
                <div className="flex justify-between gap-2">
                  <button
                    className="openFiltersButton"
                    onClick={() => setFiltersOpen(true)}
                  >
                    <IconEdit className="h-[15px] w-[15px] mr-4" />
                    Suodattimet
                  </button>
                  <span className="hidden lg:inline mr-1">Järjestys:</span>
                  <SortBy
                    items={[
                      {label: 'Suositeltu', value: productsIndexName},
                      {
                        label: 'Halvin ensin',
                        value: `${productsIndexName}_price_asc`,
                      },
                      {
                        label: 'Kallein ensin',
                        value: `${productsIndexName}_price_desc`,
                      },
                    ]}
                  />
                </div>
              </div>
              <CurrentFilters />
              <NoResultsBoundary fallback={<NoResults />}>
                <InfiniteHits
                  hitComponent={({hit}: {hit: ProductHit}) => (
                    <AlgoliaProductCard
                      product={hit}
                      showVariantTitle={false}
                    />
                  )}
                  showPrevious={false}
                  classNames={{
                    list: 'grid grid-cols-2 gap-8 md:grid-cols-3',
                    disabledLoadMore: 'hidden',
                  }}
                  translations={{
                    showMoreButtonText: 'Lataa lisää',
                  }}
                />
              </NoResultsBoundary>
              {shopifyCollection?.descriptionHtml && (
                <div id="fullDesc" className="pt-16">
                  <h2>{shopifyCollection.title}</h2>
                  <div
                    className="[&>*]:pb-4"
                    dangerouslySetInnerHTML={{
                      __html: shopifyCollection.descriptionHtml,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    </InstantSearch>
  );
}

function NoResultsBoundary({
  children,
  fallback,
}: {
  children: ReactElement;
  fallback: ReactElement | null;
}) {
  const {results} = useInstantSearch();
  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned yet.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  return (
    <div className="md:min-h-[500px] py-40 text-center text-3xl text-textInactive">
      <IconTrack className="inline-block w-10 h-10 mb-4" />
      <p>Tuotteita ei löytynyt. Säädä suodattimia.</p>
    </div>
  );
}
